import React from "react";
import { Button, capitalize, FormControl, FormHelperText, InputLabel, TextField } from "@mui/material";
import { useFormikContext } from "formik";
import Guard from "components/Guard/Guard";
import trim from "lodash/trim";
import { styled } from "@mui/material/styles";
import { css } from "@emotion/react";
import FlexBox from "components/FlexBox/FlexBox";

const nanoid = require("nanoid");

type Props = {
  label: string;
  fieldName: string;
  disabled?: boolean;
  required?: boolean;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onSubtitleLinkClicked?: () => void;
  isMultiline?: boolean;
};

const FormikTextInput: React.FC<Props> = ({
  label,
  fieldName,
  disabled = false,
  required = false,
  onBlur,
  onSubtitleLinkClicked,
  isMultiline = false,
}) => {
  const { touched, errors, handleChange, handleBlur: formikHandleBlur, setFieldValue, values } = useFormikContext<
    any
  >();

  const inputId = React.useMemo(() => fieldName + "-" + nanoid(5), [fieldName]);
  const hasError = touched[fieldName] && Boolean(errors[fieldName]);
  const handleBlur = React.useCallback(
    (evt: React.FocusEvent<HTMLInputElement>) => {
      // Trim text input
      setFieldValue(fieldName, trim(evt.target.value));

      // Custom blur handler
      if (onBlur) {
        onBlur(evt);
      }

      // General blur handler
      formikHandleBlur(evt);
    },
    [formikHandleBlur, onBlur, setFieldValue, fieldName]
  );

  return (
    <FormControl
      fullWidth={true}
      error={hasError}
      hiddenLabel={true}
      required={required}
      disabled={disabled}
      variant={"outlined"}
    >
      <FlexBox direction="row" justifyContent="space-between">
        <StyledInputLabel htmlFor={inputId} shrink={false}>
          {label}
        </StyledInputLabel>

        {onSubtitleLinkClicked && <Button onClick={onSubtitleLinkClicked}>Edit Existing Participant</Button>}
      </FlexBox>

      <Guard condition={disabled}>
        <TextField id={inputId} name={fieldName} type={"text"} value={values[fieldName]} disabled={true} />
      </Guard>

      <Guard condition={!disabled}>
        <TextField
          id={inputId}
          name={fieldName}
          type={"text"}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values[fieldName]}
          error={hasError}
          rows={isMultiline ? 3 : 1}
          multiline={isMultiline}
        />
      </Guard>

      {hasError && (
        <FormHelperText error={true} data-testid={`InputError${capitalize(fieldName)}`}>
          {errors[fieldName]}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default FormikTextInput;

const StyledInputLabel = styled(InputLabel)(
  ({ theme }) => css`
    font-size: 14px;
    font-weight: 600;
    color: ${theme.palette.text.primary};
  `
);
